import React from 'react'
import { Box, Tooltip } from '@mui/material'
import { useAppRole } from '../hooks/useAppRole'
import { rbacStrings } from '../i18n/rbacStrings'

export interface PermissionTooltipProps {
  allowed: boolean
  children: React.ReactNode
  // Occupe toute la largeur du parent (lignes pleine largeur, ex. commandes).
  fullWidth?: boolean
}

// Explique un bouton désactivé faute de droits. Le message n'apparaît que
// lorsque le rôle est connu : pendant le chargement (ou en 503) le bouton est
// simplement désactivé, sans message alarmant. Le <span> est requis par MUI
// pour qu'un Tooltip reçoive les events d'un enfant `disabled` ; les éléments
// désactivés y sont rendus transparents aux pointeurs, sinon certains
// navigateurs n'émettent aucun survol et l'infobulle ne s'affiche jamais.
// Le clic qui atterrit alors sur le <span> est arrêté ici : sans cela il
// remonterait au parent (ex. carte cliquable de FleetList) et déclencherait
// l'action de la ligne à la place du bouton refusé.
const stopPropagation = (e: React.SyntheticEvent) => e.stopPropagation()

export default function PermissionTooltip({
  allowed,
  children,
  fullWidth,
}: PermissionTooltipProps) {
  const { role, isResolved } = useAppRole()

  if (allowed || !isResolved) return <>{children}</>

  return (
    <Tooltip title={rbacStrings.notAllowed(role)} arrow>
      <Box
        component="span"
        data-testid="permission-denied"
        onClick={stopPropagation}
        onMouseDown={stopPropagation}
        sx={{
          display: fullWidth ? 'flex' : 'inline-flex',
          width: fullWidth ? '100%' : undefined,
          cursor: 'not-allowed',
          '& :disabled': { pointerEvents: 'none' },
        }}
      >
        {children}
      </Box>
    </Tooltip>
  )
}
